import { Dismiss12Regular } from "@fluentui/react-icons";
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { PRIORITY_LABEL, STATUS_LABEL } from "@/store/execution-task-ops";
import { shellGutter } from "@/shell/content-frame";
import { FuiButton } from "@/components/fui-control";
import { emptyFilter, scopeRef, type DueWindow, type TaskFilter, type TaskQueryContext } from "./task-query";
import { taskFieldIcon, type TaskFieldIcon } from "./task-glyphs";

export const DUE_FILTER_OPTIONS: Array<{ value: DueWindow; label: string }> = [
  { value: "any", label: "모든 마감" },
  { value: "overdue", label: "지연" },
  { value: "today", label: "오늘" },
  { value: "week", label: "7일 이내" },
  { value: "scheduled", label: "날짜 있음" },
  { value: "none", label: "날짜 미정" },
];

export interface FilterChipModel {
  key: keyof TaskFilter;
  field: TaskFieldIcon;
  label: string;
  text: string;
}

function named(labels: string[]): string | undefined {
  if (labels.length === 0) return undefined;
  if (labels.length === 1) return labels[0];
  return `${labels.length}개`;
}

/** 적용된 필터를 칩으로 읽는다. 기본값인 완료 숨김과 검색어는 칩이 아니다. */
export function filterChipModels(filter: TaskFilter, ctx: TaskQueryContext): FilterChipModel[] {
  const chips: FilterChipModel[] = [];
  const status = named(filter.statuses.map((s) => (s === "unset" ? "상태 미정" : STATUS_LABEL[s])));
  if (status) chips.push({ key: "statuses", field: "status", label: "상태", text: status });
  const priority = named(filter.priorities.map((p) => (p === "unset" ? "우선순위 미정" : PRIORITY_LABEL[p])));
  if (priority) chips.push({ key: "priorities", field: "priority", label: "우선순위", text: priority });
  if (filter.due !== "any") {
    const due = DUE_FILTER_OPTIONS.find((o) => o.value === filter.due)?.label ?? filter.due;
    chips.push({ key: "due", field: "due", label: "마감", text: due });
  }
  const assignee = named(filter.assignees.map((a) => (a === "" ? "담당 미정" : a)));
  if (assignee) chips.push({ key: "assignees", field: "assignee", label: "담당", text: assignee });
  const bucket = named(filter.bucketIds.map((id) => (id === "" ? "버킷 없음" : (ctx.project.planner.buckets.find((b) => b.id === id)?.name ?? id))));
  if (bucket) chips.push({ key: "bucketIds", field: "bucket", label: "버킷", text: bucket });
  const label = named(filter.labelIds.map((id) => ctx.project.planner.labels.find((l) => l.id === id)?.name ?? id));
  if (label) chips.push({ key: "labelIds", field: "label", label: "라벨", text: label });
  const scope = named(filter.linkedTaskIds.map((id) => {
    const ref = scopeRef(id, ctx);
    return `${ref.wbs} ${ref.name}`.trim();
  }));
  if (scope) chips.push({ key: "linkedTaskIds", field: "scope", label: "WBS", text: scope });
  if (filter.undecidedOnly) chips.push({ key: "undecidedOnly", field: "undecided", label: "미정", text: "필드만" });
  return chips;
}

export function clearFilterChip(filter: TaskFilter, key: FilterChipModel["key"]): TaskFilter {
  if (key === "due") return { ...filter, due: "any" };
  if (key === "undecidedOnly") return { ...filter, undecidedOnly: false };
  return { ...filter, [key]: [] };
}

const useStyles = makeStyles({
  bar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
    paddingTop: "2px",
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: shellGutter,
    paddingRight: shellGutter,
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    maxWidth: "240px",
    height: "24px",
    paddingLeft: "6px",
    paddingRight: "2px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
  },
  text: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  field: {
    color: tokens.colorNeutralForeground3,
  },
  value: {
    color: tokens.colorNeutralForeground1,
  },
  dismiss: {
    minWidth: "20px",
    width: "20px",
    height: "20px",
    color: tokens.colorNeutralForeground3,
  },
});

export function TaskFilterChips({
  filter,
  ctx,
  onChange,
  omit = [],
  clearTo,
}: {
  filter: TaskFilter;
  ctx: TaskQueryContext;
  onChange: (next: TaskFilter) => void;
  /** 세그먼트가 가진 축. 칩으로 다시 보여 주지 않는다. */
  omit?: Array<FilterChipModel["key"]>;
  /** 칩 「지우기」가 돌아갈 필터. 없으면 기본 필터다. */
  clearTo?: TaskFilter;
}) {
  const styles = useStyles();
  const chips = filterChipModels(filter, ctx).filter((chip) => !omit.includes(chip.key));
  if (chips.length === 0) return null;
  return (
    <div className={styles.bar} aria-label="적용된 필터">
      {chips.map((chip) => (
        <span key={chip.key} className={styles.chip}>
          {taskFieldIcon(chip.field)}
          <span className={styles.text} title={`${chip.label} ${chip.text}`}>
            <span className={styles.field}>{chip.label}</span> <span className={styles.value}>{chip.text}</span>
          </span>
          <FuiButton
            className={mergeClasses(styles.dismiss)}
            appearance="transparent"
            size="small"
            icon={<Dismiss12Regular />}
            aria-label={`${chip.label} 필터 지우기`}
            onClick={() => onChange(clearFilterChip(filter, chip.key))}
          />
        </span>
      ))}
      <FuiButton appearance="subtle" size="small" onClick={() => onChange(clearTo ?? emptyFilter())}>
        지우기
      </FuiButton>
    </div>
  );
}
